import { useEffect, useState } from "react";
import { BanknotesIcon } from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import classNames from "../../utils/classNames";
import PriceFormat from "../../utils/priceFormat";
import { changeDue, quickTenderAmounts } from "../../utils/till/changeDue";
import { keypadValue } from "../../utils/till/keypad";
import { validateCashTender } from "../../utils/till/tenderValidation";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import TillNumericKeypad from "./TillNumericKeypad";

interface CashTenderModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  total: number;
  onConfirm: (tendered: number) => void;
  isLoading: boolean;
  error?: string | null;
}

const problemText: Record<string, string> = {
  invalid: "Enter the amount handed over.",
  belowTotal: "That's less than the total.",
  aboveMax: "That's more than a single cash sale can be.",
};

/** Amount tendered in, change due out. */
export default function CashTenderModal({ open, setOpen, total, onConfirm, isLoading, error }: CashTenderModalProps) {
  const [entry, setEntry] = useState("");

  useEffect(() => {
    if (open) setEntry("");
  }, [open, total]);

  const tendered = keypadValue(entry);
  const check = validateCashTender(total, tendered);
  const change = check.ok ? changeDue(total, tendered) : null;
  const quick = quickTenderAmounts(total);

  return (
    <Modal open={open} setOpen={isLoading ? () => undefined : setOpen} width="max-w-lg">
      <div className="p-5 text-left">
        <div className="flex items-center gap-3 mb-4">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
            <BanknotesIcon className="h-7 w-7" />
          </span>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Cash payment</h3>
            <p className="text-sm text-gray-500">Total to pay <span className="font-semibold text-gray-900">{PriceFormat(total)}</span></p>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 mb-3">
          <p className="text-xs uppercase tracking-wide text-gray-500">Amount tendered</p>
          <p className="text-4xl font-semibold tabular-nums text-gray-900 min-h-[3rem]">{entry ? `£${entry}` : <span className="text-gray-300">£0.00</span>}</p>
          <p className={classNames("mt-1 text-sm", check.ok ? "text-emerald-700" : "text-gray-500")}>
            {check.ok ? <>Change due <span className="font-semibold text-lg">{PriceFormat(change ?? 0)}</span></> : entry ? problemText[check.reason] : "Tap a quick amount or type it in."}
          </p>
        </div>

        {quick.length > 0 ? (
          <div className="grid grid-cols-4 gap-2 mb-3">
            {quick.map((amount, i) => (
              <button
                key={amount}
                type="button"
                onClick={() => setEntry(amount.toFixed(2))}
                className={classNames(
                  "h-12 rounded-lg border text-base font-medium shadow-sm",
                  i === 0 ? "border-primary-300 bg-primary-50 text-primary-800" : "border-gray-300 bg-white text-gray-900 active:bg-gray-100"
                )}
              >
                {i === 0 ? "Exact" : PriceFormat(amount)}
              </button>
            ))}
          </div>
        ) : null}

        <TillNumericKeypad value={entry} onChange={setEntry} disabled={isLoading} />

        {error ? <p className="mt-3 rounded-md bg-red-50 p-2 text-sm text-red-700">{error}</p> : null}

        <div className="mt-4 flex justify-between gap-3">
          <ButtonInput label="Back" size="lg" classes="!bg-white !text-gray-700 border !border-gray-300 hover:!bg-gray-50" onClick={() => setOpen(false)} disabled={isLoading} />
          <ButtonInput
            label={check.ok ? `Take ${PriceFormat(tendered)}` : "Take cash"}
            size="lg"
            classes="flex-1 !bg-emerald-600 hover:!bg-emerald-700"
            onClick={() => check.ok && onConfirm(tendered)}
            disabled={!check.ok}
            isLoading={isLoading}
          />
        </div>
      </div>
    </Modal>
  );
}
